import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  QrCode, FileText, ShieldCheck, Download, Palette, ScanLine,
  BookOpen, GraduationCap, Presentation, ArrowRight, Lock, CheckCircle2, Link2, FolderKanban,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import api from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1582719471137-c3967ffb1c42?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwzfHxyZXNlYXJjaGVycyUyMGluJTIwbGFifGVufDB8fHx8MTc4NjYzNTUwM3ww&ixlib=rb-4.1.0&q=85";
const POSTER_IMG = "https://images.unsplash.com/photo-1582192730841-2a682d7375f9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxhY2FkZW1pYyUyMGNvbmZlcmVuY2UlMjBwcmVzZW50YXRpb258ZW58MHx8fHwxNzg2NjM1NTAzfDA&ixlib=rb-4.1.0&q=85";
const STUDENTS_IMG = "https://images.unsplash.com/photo-1741699427706-7bfb38c716d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHw0fHxzdHVkZW50JTIwbGFwdG9wJTIwbGlicmFyeXxlbnwwfHx8fDE3ODY2MzU1MDR8MA&ixlib=rb-4.1.0&q=85";

const steps = [
  { icon: QrCode, title: "Choose a type", text: "Website, DOI, article, survey, ORCID, reference page, and more." },
  { icon: Palette, title: "Customize & preview", text: "Colors, style, logo, and resolution with a live preview." },
  { icon: ScanLine, title: "We verify readability", text: "Contrast is checked and the code is decoded before download." },
  { icon: Download, title: "Download & use", text: "PNG, SVG, or high-resolution files ready for posters." },
];

const qrTypes = [
  { label: "Website Link", value: "url" },
  { label: "DOI Link", value: "doi" },
  { label: "Journal Article", value: "article" },
  { label: "Online Survey", value: "survey" },
  { label: "PDF / Document Link", value: "pdf" },
  { label: "ORCID Profile", value: "orcid" },
  { label: "Email Address", value: "email" },
  { label: "Phone Number", value: "phone" },
  { label: "Plain Text", value: "text" },
  { label: "Wi-Fi Credentials", value: "wifi" },
  { label: "Digital Contact Card", value: "vcard" },
];

const researchTools = [
  { label: "Reference Pages", desc: "Build a shareable page of formatted citations.", to: "/reference/new", icon: FileText },
  { label: "Research Projects", desc: "Manage and revisit your saved projects.", to: "/projects", icon: FolderKanban },
];

const whyItems = [
  { icon: Lock, title: "Static by design", text: "Your QR encodes the final destination directly. No redirect server, no expiry, no tracking." },
  { icon: ScanLine, title: "Verified before download", text: "Every code is decoded and contrast-checked so it scans reliably in print and on screen." },
  { icon: FileText, title: "Editable reference pages", text: "Update content anytime — the public URL and printed QR code never change." },
  { icon: ShieldCheck, title: "Privacy-first", text: "Guest QR codes are generated in your browser and never stored on our servers." },
];

export default function Home() {
  const [faqs, setFaqs] = useState([]);
  const [announcements, setAnnouncements] = useState([]);

  useEffect(() => {
    document.title = "CiteQR — Reliable QR codes for research & academic work";
    api.get("/faqs").then((r) => setFaqs(r.data.slice(0, 5))).catch(() => {});
    api.get("/public/announcements").then((r) => setAnnouncements(r.data)).catch(() => {});
  }, []);

  return (
    <div className="fade-in">
      {announcements.length > 0 && (
        <div className="bg-[#0A2540] px-4 py-2.5 text-center text-sm text-white" data-testid="announcement-banner">
          <span className="font-medium">{announcements[0].title}:</span> {announcements[0].body}
        </div>
      )}

      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:py-24 lg:px-8">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600">
              <GraduationCap className="h-3.5 w-3.5 text-[#0D9488]" /> Built for academic & professional work
            </span>
            <h1 className="mt-5 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              Create reliable QR codes for research, learning, and professional work.
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-600">
              Turn references, publications, surveys, supplemental materials, and academic resources into accessible, presentation-ready QR codes.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button asChild size="lg" className="bg-[#0D9488] text-white hover:bg-[#0F766E]">
                <Link to="/generator" data-testid="hero-create-qr">Create a QR Code <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-slate-300">
                <Link to="/reference/new" data-testid="hero-create-reference">Create a Reference Page</Link>
              </Button>
            </div>
            <p className="mt-4 text-sm text-slate-500">No account needed for basic QR codes. Free to start.</p>
          </div>
          <div className="relative">
            <img src={HERO_IMG} alt="Researchers collaborating in a laboratory" className="aspect-[4/3] w-full rounded-xl border border-slate-200 object-cover shadow-sm" loading="eager" />
            <div className="absolute -bottom-5 -left-5 hidden rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:block">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-md bg-[#0A2540]"><QrCode className="h-6 w-6 text-[#0D9488]" /></div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">Scan-verified</p>
                  <p className="text-xs text-slate-500">Decoded before every download</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">How it works</h2>
          <p className="mt-3 text-base text-slate-600">Four simple steps from idea to a scannable, print-ready code.</p>
        </div>
        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((s, i) => (
            <Card key={s.title} className="border-slate-200 shadow-sm">
              <CardContent className="p-6">
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-md bg-slate-100"><s.icon className="h-5 w-5 text-[#0A2540]" /></div>
                <p className="text-xs font-semibold text-[#0D9488]">STEP {i + 1}</p>
                <h3 className="mt-1 text-base font-semibold text-slate-900">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.text}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Posters */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:px-8">
          <img src={POSTER_IMG} alt="Presenter at an academic conference" className="aspect-[4/3] w-full rounded-xl border border-slate-200 object-cover shadow-sm" loading="lazy" />
          <div>
            <span className="inline-flex items-center gap-2 text-sm font-medium text-[#0D9488]"><Presentation className="h-4 w-4" /> QR codes for research posters</span>
            <h2 className="mt-3 text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">Make your poster interactive</h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Link your audience to full reference lists, datasets, surveys, and supplemental materials. Download high-resolution codes sized for large-format printing with a proper quiet zone.
            </p>
            <ul className="mt-6 space-y-3">
              {["High-resolution PNG & vector SVG", "Maintained quiet zone for reliable scanning", "Optional center logo with error correction"].map((t) => (
                <li key={t} className="flex items-start gap-2 text-sm text-slate-700"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#0D9488]" /> {t}</li>
              ))}
            </ul>
            <Button asChild className="mt-8 bg-[#0A2540] text-white hover:bg-[#0c2e4e]"><Link to="/generator">Start a poster QR code</Link></Button>
          </div>
        </div>
      </section>

      {/* Reference pages */}
      <section className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:px-8">
        <div>
          <span className="inline-flex items-center gap-2 text-sm font-medium text-[#0D9488]"><BookOpen className="h-4 w-4" /> Academic reference pages</span>
          <h2 className="mt-3 text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">A clean, mobile-friendly home for your citations</h2>
          <p className="mt-4 text-base leading-relaxed text-slate-600">
            Enter your title, author, and references, choose APA, MLA, Chicago, or Vancouver, and publish a shareable page with its own QR code. Edit anytime — the URL and code stay the same.
          </p>
          <ul className="mt-6 space-y-3">
            {["Reorder, edit, and delete references", "Clickable DOI and website links", "Copy or print the full list", "Stable URL that never changes"].map((t) => (
              <li key={t} className="flex items-start gap-2 text-sm text-slate-700"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#0D9488]" /> {t}</li>
            ))}
          </ul>
          <Button asChild className="mt-8 bg-[#0D9488] text-white hover:bg-[#0F766E]"><Link to="/reference/new">Create a reference page</Link></Button>
        </div>
        <img src={STUDENTS_IMG} alt="Student studying with a laptop in a library" className="aspect-[4/3] w-full rounded-xl border border-slate-200 object-cover shadow-sm" loading="lazy" />
      </section>

      {/* Supported types */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">Supported QR-code types</h2>
          <p className="mt-2 text-sm text-slate-600">Pick a type to open the generator with it ready to go.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            {qrTypes.map((t) => (
              <Link
                key={t.value}
                to={`/generator?type=${t.value}`}
                data-testid={`qr-type-chip-${t.value}`}
                className="group inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:border-[#0D9488] hover:bg-[#0D9488]/5 hover:text-[#0A2540] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0D9488] focus-visible:ring-offset-2"
              >
                <Link2 className="h-3.5 w-3.5 text-[#0D9488]" /> {t.label}
                <ArrowRight className="h-3.5 w-3.5 -ml-0.5 opacity-0 -translate-x-1 transition-all group-hover:opacity-100 group-hover:translate-x-0" />
              </Link>
            ))}
          </div>

          <h3 className="mt-12 text-lg font-semibold text-slate-900">Research tools</h3>
          <p className="mt-1 text-sm text-slate-600">Academic workflows beyond a single QR code.</p>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:max-w-3xl">
            {researchTools.map((r) => (
              <Link
                key={r.to}
                to={r.to}
                data-testid={`research-tool-${r.to.replace(/\//g, "-")}`}
                className="group flex items-start gap-4 rounded-lg border border-slate-200 bg-white p-5 transition-colors hover:border-[#0A2540] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0D9488] focus-visible:ring-offset-2"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[#0A2540]"><r.icon className="h-5 w-5 text-[#0D9488]" /></div>
                <div>
                  <div className="flex items-center gap-1.5 text-base font-semibold text-slate-900">{r.label}<ArrowRight className="h-4 w-4 text-[#0D9488] opacity-0 -translate-x-1 transition-all group-hover:opacity-100 group-hover:translate-x-0" /></div>
                  <p className="mt-1 text-sm text-slate-600">{r.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why + Privacy */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">Why CiteQR</h2>
        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {whyItems.map((w) => (
            <Card key={w.title} className="border-slate-200 shadow-sm">
              <CardContent className="p-6">
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-md bg-[#0A2540]"><w.icon className="h-5 w-5 text-[#0D9488]" /></div>
                <h3 className="text-base font-semibold text-slate-900">{w.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{w.text}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="border-t border-slate-200 bg-slate-50">
          <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">Frequently asked questions</h2>
            <Accordion type="single" collapsible className="mt-8" data-testid="home-faq">
              {faqs.map((f) => (
                <AccordionItem key={f.id} value={f.id}>
                  <AccordionTrigger className="text-left text-base font-medium text-slate-900">{f.question}</AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-slate-600">{f.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <div className="mt-6"><Link to="/faq" className="text-sm font-medium text-[#0D9488] hover:underline">See all FAQs →</Link></div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="bg-[#0A2540]">
        <div className="mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 lg:px-8">
          <h2 className="text-2xl font-semibold tracking-tight text-white md:text-3xl">Connect your audience to the research behind your work.</h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-slate-300">Start with a free, static QR code — no account required.</p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Button asChild size="lg" className="bg-[#0D9488] text-white hover:bg-[#0F766E]"><Link to="/generator">Create a QR Code</Link></Button>
            <Button asChild size="lg" variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white/10"><Link to="/register">Create an account</Link></Button>
          </div>
        </div>
      </section>
    </div>
  );
}
